import { APP_MODULES, PAGE_URL } from "@/data_source/constants";
import type { WorkspaceContextState } from "@/data_source/models/APIWorkspaceContext";

import {
  CAPABILITIES,
  hasConfiguredModule,
  resolveCapabilityAccess
} from "./capabilities";
import { matchesPermission } from "./permissions";

import type { RouterValidator } from "../../router/router_guard";

type AppModuleName = (typeof APP_MODULES)[keyof typeof APP_MODULES];

type UserPermissionState = {
  permissions?: string[];
  access_control?: {
    capabilities?: string[];
  };
};

function hasConfiguredAccess(user: UserPermissionState): boolean {
  return Boolean(
    user.permissions?.length || user.access_control?.capabilities?.length
  );
}

export function shouldRedirectToAccessNotConfigured(
  user: UserPermissionState | null | undefined
): boolean {
  return Boolean(
    user && Array.isArray(user.permissions) && !hasConfiguredAccess(user)
  );
}

export function noGuardValidationBuilder(
  opts: { fallbackPath?: string } = {}
): RouterValidator {
  return {
    validate: () => true,
    opts
  };
}

export function permissionGuardValidationBuilder(
  permissions: string[],
  opts: RouterValidator["opts"] = { fallbackPath: PAGE_URL.HOME }
): RouterValidator {
  return {
    validate: (user) =>
      Boolean(
        user?.permissions?.some((userPermission) =>
          permissions.some((permission) =>
            matchesPermission(userPermission, permission)
          )
        )
      ),
    opts
  };
}

export function moduleGuardValidationBuilder(
  moduleNames: AppModuleName[],
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts: RouterValidator["opts"] = { fallbackPath: PAGE_URL.HOME }
): RouterValidator {
  return {
    validate: (_, isModuleAuthorized) =>
      isModuleAuthorized
        ? isModuleAuthorized(moduleNames, requireWorkspaces)
        : false,
    opts
  };
}

export function assetGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: RouterValidator["opts"]
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.ASSETS_MODULE],
    requireWorkspaces,
    opts
  );
}

export function assetCapabilityGuardValidationBuilder(
  capability: string,
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts: RouterValidator["opts"] = {
    renderAccessDenied: true,
    accessDeniedMessage: "You do not have permission to access this page."
  }
): RouterValidator {
  return capabilityGuardValidationBuilder(
    capability,
    [APP_MODULES.ASSETS_MODULE],
    requireWorkspaces,
    opts
  );
}

export function workOrderGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.WORK_ORDER_MODULE],
    requireWorkspaces,
    opts
  );
}

export function siteVisitGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.SITE_VISIT_MODULE],
    requireWorkspaces,
    opts
  );
}

export function formAuditGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.FORM_AUDIT_MODULE],
    requireWorkspaces,
    opts
  );
}

export function formModuleGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.FORM_MODULE],
    requireWorkspaces,
    opts
  );
}

export function settingsGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational", "master"],
  opts?: RouterValidator["opts"]
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.SETTINGS_MODULE],
    requireWorkspaces,
    opts
  );
}

export function settingsPermissionGuardValidationBuilder(
  permissionPatterns: string[],
  requireWorkspaces: ("master" | "operational")[] = ["master"],
  opts?: RouterValidator["opts"]
): RouterValidator {
  const settingsGuard = settingsGuardValidationBuilder(requireWorkspaces, opts);

  return {
    validate: (user, isModuleAuthorized, workspace, workspaceContext) =>
      settingsGuard.validate(
        user,
        isModuleAuthorized,
        workspace,
        workspaceContext
      ) &&
      Boolean(
        permissionPatterns.every((permissionPattern) =>
          user?.permissions?.some((userPermission) =>
            matchesPermission(userPermission, permissionPattern)
          )
        )
      ),
    opts: settingsGuard.opts
  };
}
export function workspaceGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["master", "operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.WORKSPACE_MODULE],
    requireWorkspaces,
    opts
  );
}
export function reportGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.REPORTS_MODULE],
    requireWorkspaces,
    opts
  );
}
export function inventoryGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational"],
  opts?: {
    fallbackPath?: string;
  }
): RouterValidator {
  return moduleGuardValidationBuilder(
    [APP_MODULES.INVENTORY_MODULE],
    requireWorkspaces,
    opts
  );
}
function isWorkspaceAllowed(
  requireWorkspaces: readonly ("master" | "operational")[],
  workspace: Parameters<RouterValidator["validate"]>[2],
  workspaceContext: WorkspaceContextState | null | undefined
): boolean {
  if (workspaceContext) {
    return requireWorkspaces.some((requireWorkspace) =>
      requireWorkspace === "master"
        ? workspaceContext.resolvedContextType === "master" &&
          workspaceContext.canAccessMaster
        : workspaceContext.resolvedContextType === "operational" &&
          workspace?.can_operate !== false
    );
  }

  return requireWorkspaces.some((requireWorkspace) =>
    requireWorkspace === "master"
      ? Boolean(workspace?.is_master)
      : Boolean(workspace?.can_operate && !workspace?.is_master)
  );
}

export function capabilityGuardValidationBuilder(
  capability: string,
  moduleNames: readonly AppModuleName[],
  requireWorkspaces: readonly ("master" | "operational")[] = ["operational"],
  opts: RouterValidator["opts"] = {
    renderAccessDenied: true,
    accessDeniedMessage: "You do not have permission to access this page."
  }
): RouterValidator {
  return capabilitiesGuardValidationBuilder(
    [capability],
    moduleNames,
    requireWorkspaces,
    opts
  );
}

export function capabilitiesGuardValidationBuilder(
  capabilities: readonly string[],
  moduleNames: readonly AppModuleName[],
  requireWorkspaces: readonly ("master" | "operational")[] = ["operational"],
  opts: RouterValidator["opts"] & {
    requireAll?: boolean;
    requireAllModules?: boolean;
    /** Capabilities required in addition to the any-of/all-of `capabilities`. */
    alsoRequire?: readonly string[];
  } = {
    renderAccessDenied: true,
    accessDeniedMessage: "You do not have permission to access this page."
  }
): RouterValidator {
  return {
    validate: (user, _isModuleAuthorized, workspace, workspaceContext) => {
      if (
        !user ||
        !isWorkspaceAllowed(requireWorkspaces, workspace, workspaceContext)
      ) {
        return false;
      }

      const isModuleConfigured = (modules: readonly string[]) =>
        hasConfiguredModule({
          effectiveModules: user.access_control?.effective_modules,
          companyModules: user.company?.modules,
          modules
        });
      const hasRequiredModule = opts.requireAllModules
        ? moduleNames.length > 0 &&
          moduleNames.every((moduleName) => isModuleConfigured([moduleName]))
        : isModuleConfigured(moduleNames);

      if (!hasRequiredModule) return false;

      const hasCapability = (capability: string) =>
        resolveCapabilityAccess({
          userCapabilities: user.access_control?.capabilities,
          capabilities: [capability],
          whenContractMissing: "deny"
        });

      if (opts.alsoRequire && !opts.alsoRequire.every(hasCapability)) {
        return false;
      }
      return opts.requireAll
        ? capabilities.every(hasCapability)
        : capabilities.some(hasCapability);
    },
    failureReason: (user) => {
      const accessControl = user?.access_control;
      if (
        accessControl?.is_available === false ||
        accessControl?.profile_status === "not_configured"
      ) {
        return "access_not_configured";
      }
      return "access_denied";
    },
    opts
  };
}

export function formAnalysisGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational", "master"],
  opts?: RouterValidator["opts"]
): RouterValidator {
  return capabilityGuardValidationBuilder(
    "form_analysis.view",
    [APP_MODULES.FORM_AUDIT_MODULE],
    requireWorkspaces,
    opts
  );
}

export function documentsGuardValidationBuilder(
  requireWorkspaces: ("master" | "operational")[] = ["operational", "master"],
  opts?: RouterValidator["opts"]
): RouterValidator {
  return capabilityGuardValidationBuilder(
    "documents.view",
    [APP_MODULES.DOCUMENTS_MODULE],
    requireWorkspaces,
    opts
  );
}
export function onlyMasterGuardValidationBuilder(opts?: {
  fallbackPath?: string;
}): RouterValidator {
  return {
    validate: (
      _,
      __,
      workspace,
      workspaceContext?: WorkspaceContextState | null
    ) => {
      if (workspaceContext) {
        return (
          workspaceContext.resolvedContextType === "master" &&
          workspaceContext.canAccessMaster
        );
      }

      return !!workspace?.is_master;
    },
    opts: opts ?? {}
  };
}
export function onlyNotPermissionUser(opts?: {
  fallbackPath?: string;
}): RouterValidator {
  return {
    validate: (user) => {
      return (!!user && !hasConfiguredAccess(user)) || !user;
    },
    opts: opts ?? {
      fallbackPath: PAGE_URL.HOME
    }
  };
}
export function onlyPermissionUser(): RouterValidator {
  return {
    validate: (user) => {
      return (!!user && hasConfiguredAccess(user)) || !user;
    },
    opts: {
      fallbackPath: PAGE_URL.ACCESS_NOT_CONFIGURED
    }
  };
}

function timesheetModuleConfigured(
  user: Parameters<RouterValidator["validate"]>[0]
): boolean {
  return hasConfiguredModule({
    effectiveModules: user?.access_control?.effective_modules,
    companyModules: user?.company?.modules,
    modules: [APP_MODULES.TIMESHEET_MODULE]
  });
}

/**
 * Operational workspaces need `timesheets.view`. All Workspaces needs
 * `timesheets.view_all_workspaces`. A missing module is Access Not Configured;
 * a missing capability is Access Denied.
 */
export function timesheetGuardValidationBuilder(
  opts?: RouterValidator["opts"]
): RouterValidator {
  return {
    validate: (user, _isModuleAuthorized, workspace, workspaceContext) => {
      if (
        !user ||
        !isWorkspaceAllowed(
          ["operational", "master"],
          workspace,
          workspaceContext
        ) ||
        !timesheetModuleConfigured(user)
      ) {
        return false;
      }

      const isMaster = workspaceContext
        ? workspaceContext.resolvedContextType === "master" &&
          workspaceContext.canAccessMaster
        : Boolean(workspace?.is_master);
      const required = isMaster
        ? CAPABILITIES.TIMESHEETS.VIEW_ALL_WORKSPACES
        : CAPABILITIES.TIMESHEETS.VIEW;

      return resolveCapabilityAccess({
        userCapabilities: user.access_control?.capabilities,
        capabilities: [required],
        whenContractMissing: "deny"
      });
    },
    failureReason: (user) => {
      if (!user) return "access_denied";
      const accessControl = user.access_control;
      if (
        accessControl?.is_available === false ||
        accessControl?.profile_status === "not_configured" ||
        !timesheetModuleConfigured(user)
      ) {
        return "access_not_configured";
      }
      return "access_denied";
    },
    opts: {
      renderAccessDenied: true,
      accessDeniedMessage: "You do not have permission to access this page.",
      ...opts
    }
  };
}

/**
 * `Settings → Timesheets`: operational workspaces only (Backend rejects the
 * Master Space for settings, Kiosk and PIN). It needs the contracted module and
 * `timesheets.view`, which is what Backend requires to read the settings; each
 * mutation is gated again by its own capability. A missing module is Access Not
 * Configured; a missing capability is Access Denied.
 */
export function timesheetSettingsGuardValidationBuilder(
  opts?: RouterValidator["opts"]
): RouterValidator {
  return {
    validate: (user, _isModuleAuthorized, workspace, workspaceContext) => {
      if (
        !user ||
        !isWorkspaceAllowed(["operational"], workspace, workspaceContext) ||
        !timesheetModuleConfigured(user)
      ) {
        return false;
      }

      return resolveCapabilityAccess({
        userCapabilities: user.access_control?.capabilities,
        capabilities: [CAPABILITIES.TIMESHEETS.VIEW],
        whenContractMissing: "deny"
      });
    },
    failureReason: (user) => {
      if (!user) return "access_denied";
      const accessControl = user.access_control;
      if (
        accessControl?.is_available === false ||
        accessControl?.profile_status === "not_configured" ||
        !timesheetModuleConfigured(user)
      ) {
        return "access_not_configured";
      }
      return "access_denied";
    },
    opts: {
      renderAccessDenied: true,
      accessDeniedMessage: "You do not have permission to access this page.",
      ...opts
    }
  };
}
